import type { ControlPlaneOperationalProjection } from '@internal/spassu-code-contract';
import { Box, Typography, makeStyles } from '@material-ui/core';
import {
  ATTEMPT_STATE_LABELS,
  BOOTSTRAP_ACTION_LABELS,
  BOOTSTRAP_STAGE_LABELS,
  WORKSPACE_LABELS,
  formatManagementInstant,
} from './control-plane-management-labels';
import {
  StateDimension,
  useStateDimensionStyles,
} from './ControlPlaneStateDimension';
import { ConnectionSummary } from './ControlPlaneConnectionSummary';

const useStyles = makeStyles(theme => ({
  panel: { display: 'grid', gap: theme.spacing(2) },
}));

export { ManagementTechnicalDetails } from './ControlPlaneTechnicalDetails';
export { CapabilityButton } from './ControlPlaneCapabilityButton';
export {
  ACTIVITY_CYCLE_LABELS,
  CONNECTION_LABELS,
  EXPIRY_BAND_LABELS,
  WORKSPACE_LABELS,
} from './control-plane-management-labels';

/**
 * Lean state summary: connection, activity cycle and — only when the canonical
 * projection reports one — the expiry instant and band (RN-CONEX-03). The
 * attempt and the local workspace appear only when there is something to say;
 * everything else lives in `ManagementTechnicalDetails`, which the page renders
 * last and collapsed (RN-CONEX-04).
 */
export function ManagementStatePanel(props: {
  projection: ControlPlaneOperationalProjection;
  stale: boolean;
}) {
  const classes = useStyles();
  return (
    <Box className={classes.panel} data-testid="management-state">
      <ConnectionSummary projection={props.projection} stale={props.stale} />
      <AttemptDimension projection={props.projection} />
      <WorkspaceDimension projection={props.projection} />
    </Box>
  );
}

function WorkspaceDimension(props: {
  projection: ControlPlaneOperationalProjection;
}) {
  const { workspace } = props.projection;
  if (workspace.state === 'not_requested') return null;
  return (
    <StateDimension
      label="Workspace local"
      value={WORKSPACE_LABELS[workspace.state]}
      detail={
        workspace.nextAttemptAt
          ? `Próxima tentativa: ${formatManagementInstant(
              workspace.nextAttemptAt,
            )}`
          : workspace.lastErrorCode
      }
    />
  );
}

function AttemptDimension(props: {
  projection: ControlPlaneOperationalProjection;
}) {
  const classes = useStateDimensionStyles();
  const attempt = props.projection.attempt;
  // A committed attempt is already told by the connection chip; only an
  // attempt still in progress or failed adds information.
  if (!attempt || attempt.state === 'succeeded') return null;
  const detail = attemptDetail(attempt);
  const content = (
    <Box className={classes.dimension}>
      <Typography variant="overline" component="h3">
        Tentativa de conexão
      </Typography>
      <Typography variant="body1">
        {ATTEMPT_STATE_LABELS[attempt.state]}
      </Typography>
      <Typography variant="body2">
        {BOOTSTRAP_STAGE_LABELS[attempt.stage]}
      </Typography>
      <Typography variant="caption" className={classes.reason}>
        {detail}
      </Typography>
      {attempt.purpose === 'renewal' && attempt.state === 'failed' ? (
        <Typography variant="caption">
          A conexão anterior permanece disponível enquanto ainda estiver válida.
        </Typography>
      ) : null}
    </Box>
  );
  return attempt.state === 'failed' ? (
    <Box role="alert">{content}</Box>
  ) : (
    <Box aria-live="polite" aria-atomic="true">
      {content}
    </Box>
  );
}

function attemptDetail(
  attempt: NonNullable<ControlPlaneOperationalProjection['attempt']>,
): string {
  if (attempt.diagnostic) {
    return `${attempt.diagnostic.code} · ${
      BOOTSTRAP_ACTION_LABELS[attempt.diagnostic.actionCode]
    }`;
  }
  if (attempt.state === 'failed') {
    return 'A tentativa não foi concluída. Entre em contato com o suporte.';
  }
  return `Última mudança: ${formatManagementInstant(attempt.stageChangedAt)}`;
}
